import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Button,
  Center,
  Group,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { useDocumentTitle } from "@mantine/hooks";
import {
  IconExclamationCircle,
  IconInfoCircle,
  IconLock,
  IconLockCheck,
} from "@tabler/icons-react";
import { jwtDecode } from "jwt-decode";
import i18n from "src/i18n";
import { z } from "zod";

import { StyledSignIn } from "@pages/SignIn/SignIn.styled";

import { LanguageToggle } from "@components/LanguageToggle";

import { showErrorNotification } from "@utils/showNotification";

import { PAGE_TITLE } from "@constants/index";

import { useResetPassword } from "@hooks/mutations/user/useResetPassword";
import { useCountDown } from "@hooks/useCountDown";

import Logo from "@assets/misc/logo.webp";


const schema = () =>
  z
    .object({
      password: z.string().min(5, i18n.t("password-min-5-chars")),
      confirmPassword: z.string().min(1, i18n.t("confirm-password-required")),
    })
    .refine(data => data.password === data.confirmPassword, {
      message: i18n.t("passwords-mismatch"),
      path: ["confirmPassword"], // path of error
    });

type Inputs = z.infer<ReturnType<typeof schema>>;

const COUNTDOWN = 7;

export const ResetPassword = () => {
  const { t } = useTranslation();
  useDocumentTitle(`${t("reset-password")} • ${PAGE_TITLE}`);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const [isTokenMissing, setIsTokenMissing] = useState(false);
  const [isTokenError, setIsTokenError] = useState(false);
  const [username, setUsername] = useState("");
  const [token, setToken] = useState("");
  const { count, setStartCounting } = useCountDown({ seconds: COUNTDOWN });

  const { mutate: resetPassword, isLoading, isSuccess } = useResetPassword();

  const form = useForm({
    mode: "uncontrolled",
    validate: zodResolver(schema()),
    initialValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const handleFormSubmit = (values: Inputs) => {
    if (isTokenExpired || isTokenError || isTokenMissing) return;
    // console.log(values);
    resetPassword(
      { password: values.confirmPassword, token },
      {
        onSuccess: () => {
          form.reset();
          setStartCounting(true);
          setTimeout(() => {
            navigate("/", { state: { username } });
          }, 1000 * COUNTDOWN);
        },
      }
    );
  };

  useEffect(() => {
    if (searchParams.toString()) {
      // console.log(searchParams.toString());
      // console.log(searchParams.toString().split("=")[1]);
      try {
        const res: any = jwtDecode(searchParams.toString().split("=")[1]);
        // console.log("Token expires on:", new Date(res.exp * 1000));
        if (res.exp * 1000 < new Date().getTime()) {
          setIsTokenExpired(true);
        } else {
          setIsTokenExpired(false); // Just to be safe
          setUsername(res.username as string);
          setToken(searchParams.toString().split("=")[1]);
        }
      } catch (error) {
        console.error("[Reset password]", error);
        setIsTokenError(true);
        showErrorNotification({ title: "oops", message: "reset-link-error" });
      }
    } else {
      setIsTokenMissing(true); // Just to be safe
    }
  }, [searchParams]);

  return (
    <StyledSignIn>
      <LanguageToggle />

      <div className="content">
        <div className="logo">
          <img src={Logo} alt="La Ruche Health" />
        </div>

        {!isSuccess ? (
          <>
            <h1 className="heading">{t("reset-password")}</h1>
            <p className="sub-heading">{t("create-strong-password")}</p>

            {isTokenExpired && (
              <Alert
                mt={"md"}
                variant="light"
                color="red"
                icon={<IconExclamationCircle />}>
                <Text fz={14} lh={1.5} c="red">
                  {t("reset-link-expired")}
                </Text>
              </Alert>
            )}
            {isTokenMissing && (
              <Alert
                mt={"md"}
                variant="light"
                color="red"
                icon={<IconExclamationCircle />}>
                <Text fz={14} lh={1.5} c="red">
                  {t("reset-link-missing")}
                </Text>
              </Alert>
            )}

            <form
              className="form"
              onSubmit={form.onSubmit(
                values => handleFormSubmit(values),
                errors => {
                  const firstErrorPath = Object.keys(errors)[0];
                  form.getInputNode(firstErrorPath)?.focus();
                }
              )}>
              <TextInput
                label={t("username")}
                description={t("dont-forget-username")}
                value={username ?? ""}
                // disabled
                readOnly
                rightSection={
                  <Tooltip
                    label={t("username-immutable")}
                    withArrow
                    events={{ hover: true, focus: true, touch: true }}>
                    <IconInfoCircle size={16} />
                  </Tooltip>
                }
              />
              <PasswordInput
                label={t("password")}
                withAsterisk
                leftSection={<IconLock size={16} />}
                {...form.getInputProps("password")}
                key={form.key("password")}
              />
              <PasswordInput
                label={t("confirm-password")}
                withAsterisk
                leftSection={<IconLock size={16} />}
                {...form.getInputProps("confirmPassword")}
                key={form.key("confirmPassword")}
              />

              <Button
                type="submit"
                mt={"lg"}
                loading={isLoading}
                disabled={isTokenExpired || isTokenError || isTokenMissing}>
                {t("reset-password")}
              </Button>
            </form>
          </>
        ) : (
          <Stack mt="xl" gap="0">
            <Center>
              <Group
                justify="center"
                align="center"
                bg="green.0"
                style={{ borderRadius: "50%", height: 80, width: 80 }}>
                <IconLockCheck
                  size={35}
                  color={"var(--mantine-color-green-6)"}
                  stroke={1.5}
                />
              </Group>
            </Center>
            <Stack gap="xs" mt="md">
              <Title order={1} fw={500} fz={20} mt="md">
                {t("password-updated")}
              </Title>
              <Text c="dimmed" fz={15} lh={1.5}>
                {t("redirected-in-x", { count })}
              </Text>
            </Stack>
          </Stack>
        )}
      </div>
    </StyledSignIn>
  );
};
